import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '@/store';
import {
  useLazyListConversationsQuery,
  useLazyGetConversationQuery,
  useSendMessageMutation,
  syncHookFormAsQuery,
} from '@/store/api/admin/messagesApi';
import type { IConversation, SortType } from '@/types/admin/messages';
import { useOptimizedPolling } from '@/hooks/admin/useAppState';

type FilterType = 'open' | 'archived' | 'assigned';

export const useResidentConversations = () => {
  const [conversationQuery, setConversationQuery] = useState('');
  const [sortType, setSortType] = useState<SortType>('latest');
  const [filterType, setFilterType] = useState<FilterType>('open');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const [allConversations, setAllConversations] = useState<IConversation[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  
  // Prevent automatic loading immediately after sort/search changes
  const [canLoadMore, setCanLoadMore] = useState(true);
  
  // Track when we're resetting to prevent race conditions
  const isResettingRef = useRef(false);

  const [getConversations, { data, isLoading, isFetching, error }] = useLazyListConversationsQuery();

  const propertyId = useAppSelector((s) => s.auth.user?.propertyId);

  const fetchConversations = useCallback(
    (pageNum: number = 0) => {
      if (!propertyId) return;
      
      // Match resio-web parameters exactly - only include essential params
      const searchParams = syncHookFormAsQuery({
        propertyId: propertyId.toString(),
        pageNum: pageNum.toString(),
        isAdmin: 'false',
        sortType,
        filterType,
        // Only include searchQuery if it exists (match resio-web behavior)
        ...(conversationQuery && { searchQuery: conversationQuery }),
      });
      
      getConversations(searchParams.toString());
    },
    [propertyId, conversationQuery, sortType, filterType, getConversations]
  );

  // Helper: compute last activity timestamp for a conversation
  const getLastActivity = useCallback((c: IConversation) => {
    const lastMsg = c.messages && c.messages.length > 0 ? c.messages[c.messages.length - 1] : undefined;
    const ts = (lastMsg as any)?.createdAt || (c as any).updatedAt || (c as any).createdAt;
    return ts ? new Date(ts).getTime() : 0;
  }, []);

  const sortByLastActivityDesc = useCallback((list: IConversation[]) => {
    return [...list].sort((a, b) => getLastActivity(b) - getLastActivity(a));
  }, [getLastActivity]);

  // Reset and fetch when search/sort/property changes - always start from page 0
  useEffect(() => {
    isResettingRef.current = true; // Mark that we're resetting
    setPage(0);
    setHasNextPage(true);
    setAllConversations([]);
    setCanLoadMore(false); // Prevent immediate auto-loading
    fetchConversations(0); // Always fetch page 0 for new search/sort
  }, [conversationQuery, sortType, propertyId, fetchConversations]);

  const refetchInitial = useCallback(() => {
    // Keep existing items visible during refresh to avoid empty flashes
    isResettingRef.current = true;
    setPage(0);
    setHasNextPage(true);
    setCanLoadMore(false);
    fetchConversations(0);
  }, [fetchConversations]);

  // Only fetch additional pages when page > 0 (for "load more" functionality)
  useEffect(() => {
    if (page > 0 && !isResettingRef.current) {
      fetchConversations(page);
    }
  }, [page, fetchConversations]);

  useEffect(() => {
    if (!data?.response) return;
    const newConversations = data.response as IConversation[];

    if (isResettingRef.current || page === 0) {
      // Initial load or explicit refresh: replace and sort
      const sorted = sortByLastActivityDesc(newConversations);
      setAllConversations(sorted);
      isResettingRef.current = false;
      setTimeout(() => setCanLoadMore(true), 100);
    } else {
      // Merge pages: upsert by id, then sort
      setAllConversations((prev) => {
        const byId = new Map<number, IConversation>(prev.map((c) => [c.id as number, c]));
        for (const c of newConversations) {
          byId.set(c.id as number, c);
        }
        const merged = Array.from(byId.values());
        return sortByLastActivityDesc(merged);
      });
    }

    setHasNextPage(newConversations.length === pageSize);
  }, [data, page, sortByLastActivityDesc]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage && canLoadMore) {
      setPage((p) => p + 1);
    }
  }, [isLoading, hasNextPage, canLoadMore]);

  // Optimistically mark a conversation as read in local state
  // This provides instant UI feedback without waiting for API or refetch
  const markConversationAsReadLocally = useCallback((conversationId: number) => {
    setAllConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, hasUnread: false }
          : conv
      )
    );
  }, []);

  return {
    conversations: allConversations,
    isLoading,
    isFetching,
    error,
    conversationQuery,
    setConversationQuery,
    filterType,
    setFilterType,
    sortType,
    setSortType,
    hasNextPage,
    loadMore,
    refetchInitial,
    markConversationAsReadLocally,
  } as const;
};

export const useResidentConversation = (conversationId?: number, options?: { skip?: boolean }) => {
  const [getConversation, { data, isLoading, error }] = useLazyGetConversationQuery();
  const skip = options?.skip ?? false;

  const fetchConversation = useCallback(() => {
    if (conversationId && !skip) {
      getConversation({ conversationId });
    }
  }, [conversationId, getConversation, skip]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Poll for updates while the app is active, a conversation is selected, and not skipped
  useOptimizedPolling(
    fetchConversation,
    10000, // 10s interval to match web behavior
    !!conversationId && !skip
  );

  return {
    conversation: data?.response as IConversation | undefined,
    isLoading,
    error,
    refetch: fetchConversation,
  } as const;
};

export const useResidentMessaging = () => {
  const [sendMessage, sendState] = useSendMessageMutation();
  return useMemo(() => ({ sendMessage, ...sendState }), [sendMessage, sendState]);
};

/**
 * Hook to find an existing conversation by subject prefix.
 * Used to check if a conversation already exists (e.g., for maintenance requests)
 * before creating a new one.
 */
export const useFindConversationBySubject = () => {
  const [getConversations] = useLazyListConversationsQuery();
  const propertyId = useAppSelector((s) => s.auth.user?.propertyId);
  const [isSearching, setIsSearching] = useState(false);

  const findBySubject = useCallback(
    async (subjectPrefix: string): Promise<number | null> => {
      if (!propertyId) return null;

      setIsSearching(true);
      try {
        // Search for conversations matching the subject prefix
        const searchParams = syncHookFormAsQuery({
          propertyId: propertyId.toString(),
          pageNum: '0',
          isAdmin: 'false',
          sortType: 'latest',
          filterType: 'open',
          searchQuery: subjectPrefix,
        });

        const result = await getConversations(searchParams.toString()).unwrap();
        const conversations = result?.response as IConversation[] | undefined;

        if (conversations && conversations.length > 0) {
          // Find a conversation whose subject starts with the prefix
          const match = conversations.find((c) =>
            c.subject?.startsWith(subjectPrefix)
          );
          return match?.id ?? null;
        }
        return null;
      } catch (error) {
        console.error('Error finding conversation by subject:', error);
        return null;
      } finally {
        setIsSearching(false);
      }
    },
    [getConversations, propertyId]
  );

  return { findBySubject, isSearching };
};
